// Copyright (c) 2021-2024 Visual Intelligence LP. All rights reserved.
//
import * as React from "react";

import container from '@core/di';
import AuthStore from '@core/stores/auth';
import { GridComponent, GroupSettingsModel } from "@syncfusion/ej2-react-grids";
import { head } from "lodash";
import { mkUserKey, readFrLocalStorage } from "@shared/utils/common";
import { SHEETS_HEADINGS_HEIGHT, GRID_STATUS_COLUMN_LABELS } from "@shared/constants/sp-sheets";
import { ClickEventArgs } from "@syncfusion/ej2-react-navigations";
import { NotificationType, showNotification } from "@shared/components/Notification";
import { TOWER_FIELDS } from '@shared/constants/asset';
import { SInformationDTO } from "@shared/models/sp-sheets";
import { ComboBox, DropDownList } from "@syncfusion/ej2-react-dropdowns";
import { L10n } from '@syncfusion/ej2-base';
import { ISelectedCell } from '@syncfusion/ej2-grids';
// Global/Export functions. TBD: move to a util file.

export class CollapsedItem {
  public MappingName: string;
  public CustomName: string;
}

export class MyGridGroupItem {
  public level: number;
  public caption: string;
}


// BaseClass for all grid-tab classes.
// TBD: move common code from the grid-tab classes into this baseClass.
//      Currently, only have 'grid' and 'authStore', and 
//      define 'mkUserKey' to be used by all classes to make keys for localStorage.
abstract class SBase<P = unknown, S = unknown> extends React.Component<P, S> {
  public grid: GridComponent | null;     // handle to the grid (ie. sheet).
  //public state: {grouping: string};      // Stateful var to keep the current grouping.

  // Store objs to query for data from portal-api.
  protected authStore = container.get<AuthStore>(AuthStore.diToken);

  protected userID;

  protected initialLoad: boolean = false;                     // Flag to collapse groups on load.

  // Vars to setup columns.
  protected columnNames: Array<string>;
  protected columnLabels: Array<string>;
  protected columnVisibility: Array<boolean>;
  protected initialWidths: Array<string>;

  // Vars to setup grouping.
  protected groupOptions: GroupSettingsModel;
  protected lastGroupItems: Array<MyGridGroupItem>;
  protected collapsedItems: Array<CollapsedItem>;
  protected saveExpandedItems: boolean = false;

  // Remember scrolling position
  protected lastVScrollPosition: number;
  protected scrollingPositionKey: string = '';

  // Vars for cut-and-paste.
  protected selectedCellIndex: { row: number, col: number};
  protected copiedCellIndex: { row: number, col: number};
  protected copiedCellColumnName: string;
  protected selectedCellIndexes: Array<{ row: number, col: number}>;
  protected copiedCellIndexes: Array<ISelectedCell> | undefined;
  protected copiedCellColumnNames: Array<string>;
  protected toolbar: Array<Object>;                 // Shared toolbar for all tabs
  protected toolbarReduced: Array<Object>;

  constructor(props: P) {
    super(props);

    const { user } = this.authStore;
    this.userID = user.id.toString();
    this.lastGroupItems = [];

    this.lastVScrollPosition = 0;

    this.initialLoad = true;
    this.selectedCellIndex = { row: -1, col: -1 };
    this.copiedCellIndex = { row: -1, col: -1 };
    this.copiedCellColumnName = '';
    this.toolbar = [
      'Cancel',
      'Update',
      // { text: 'Copy',         prefixIcon: 'e-copy',       id: 'copy',     disabled: true, tooltipText: 'Copy a source cell to destination cells' },
      // { text: 'Paste',        prefixIcon: 'e-paste',      id: 'paste',    disabled: true, tooltipText: 'Paste the content of the source cell to  cells in the same column only.' },
      { text: 'Collapse all', prefixIcon: 'e-collapse-2', id: 'collapse', disabled: true, tooltipText: 'Collapse any expanded items.' },
      //{ text: 'OTiQ', prefixIcon: 'e-hyperlink-open', id: 'otiq', tooltipText: 'Open OTIQ site.' },
      'ColumnChooser',
    ];

    this.toolbarReduced = [
      'Cancel',
      'Update',
      { text: 'Collapse all', prefixIcon: 'e-collapse-2', id: 'collapse', disabled: true, tooltipText: 'Collapse any expanded items.' },
      //{ text: 'OTiQ', prefixIcon: 'e-hyperlink-open', id: 'otiq', tooltipText: 'Open OTIQ site.' },
      'ColumnChooser',
    ];

    // Redefine default texts in locale
    L10n.load({
      'en-US': {
        'grid': {
          'Add': 'New'
        }
      }
    });
  }

  setupCopyAndPaste(enable?: boolean, startPos?: number) {
    if (enable ?? true) {
      this.toolbar.splice(startPos ?? 2, 0, { text: 'Paste', prefixIcon: 'e-paste', id: 'paste', disabled: true, tooltipText: 'Paste the content of the source cell to  cells in the same column only.' });
      this.toolbar.splice(startPos ?? 2, 0, { text: 'Copy',  prefixIcon: 'e-copy',  id: 'copy',  disabled: true, tooltipText: 'Copy a source cell to destination cells' });
    }
  }

  getDisplaySites() {
    if (this.grid !== null) {
      const currentViewData = this.grid?.getCurrentViewRecords(); 
      const persistData = JSON.parse(this.grid.getPersistData());
      const item = persistData.columns.find(col => col.name = TOWER_FIELDS.name);
      //const filters = JSON.parse(this.grid.getPersistData()).filterSettings.columns.filter(function (f) { return f.field == 'name' });
      const filters = currentViewData.map(site => ({
        field: TOWER_FIELDS.name,
        ignoreAccent: false,
        matchCase: false,
        operator: 'equal',
        predicate: 'or',
        type: 'string',
        uid: item.uid,
        value: site['name']
      }));
      return JSON.stringify(filters);  
    }
    return '';
  }

  // Add handler for 'resize' to adjust grid height to fit the browser client window.
  componentDidMount() {  
    window.addEventListener('resize', this.onresize.bind(this));

    // Check and enable 'Collapse all'
    this.enableCollapseAll(true);
  }  

  // Adjust grid height to fit window
  onresize(e) {      
    if (this.grid && this.grid.height)
    {
      this.grid.height = Number(window.innerHeight - SHEETS_HEADINGS_HEIGHT);  
    } 
  } 

  componentWillUnmount() {
    window.removeEventListener('resize', this.onresize);
    closeAllTooltipss(this.grid);
  }

  // Build the edit params for a cell holding a selection box.
  // The first two arguments may be created/modified (which lint packages may mark as questionable practice).
  BuildComboParams(comboElem: HTMLElement | undefined, comboObj: ComboBox | undefined, possibleVals: Array<string>, colName: string) 
  {
    return ({
      // Create a combo element.
      create: () => {
        comboElem = document.createElement("combo");
        return comboElem;
      },

      // Return the selected value.
      read: () => {
        if (comboObj) {
          console.log("*** check read combo.value:", comboObj.value);
          return comboObj.value;
        } else {
          console.log("*** check read combo.value:", undefined);
          return undefined;
        }
      },

      // Free the combo obj.
      destroy: () => {
        comboObj?.destroy();
      },

      // Show the combo obj.
      write: (args: any) => {
        //console.log("****** check compStat combobox write args:", args, "possible status:", this.statusStrings);

        comboObj = new ComboBox({
          dataSource: possibleVals,            // Add items.              
          value: args.rowData[colName],   // Set current item.
        });
        comboObj.appendTo(comboElem);
      },
    })
  }

  // Build the edit params for a cell holding a selection box.
  // The first two arguments may be created/modified (which lint packages may mark as questionable practice).
  BuildDropdownParams(dropdownElem: HTMLElement | undefined, dropdownObj: DropDownList | undefined, possibleVals: Array<string>, colName: string, placeholder: string) 
  {
    return ({
      // Create a dropdownList element.
      create: () => {
        dropdownElem = document.createElement("combo");
        return dropdownElem;
      },

      // Return the selected value.
      read: () => {
        if (dropdownObj) {
          console.log("*** check read combo.value:", dropdownObj.value);
          return dropdownObj.value;
        } else {
          console.log("*** check read combo.value:", undefined);
          return undefined;
        }
      },

      // Free the combo obj.
      destroy: () => {
        dropdownObj?.destroy();
      },

      // Show the dropdownList obj.
      write: (args: any) => {
        //console.log("****** check compStat combobox write args:", args, "possible status:", this.statusStrings);

        dropdownObj = new DropDownList({
          dataSource: possibleVals,           // Add items.              
          value: args.rowData[colName],       // Set current selection.
          placeholder: placeholder
        });
        dropdownObj.appendTo(dropdownElem);
      },
    })
  }

  restoreCollapseExpand() {
    if (!this.grid)
      return;

    let counts = 0;

    //Save the expanded items => must collapse all first
    if (this.saveExpandedItems) {
      try {
        this.grid?.groupCollapseAll();
      }
      catch (err) {
        console.log('****** Exception in restoreCollapseExpand:', err);
      }
    }

    if (this.collapsedItems && this.collapsedItems.length > 0) {
      // Collapse or Expand items (depends on flag saveExpandedItems)
      counts = restoreCollapsedItems(this.grid, this.collapsedItems, this.saveExpandedItems);
      //this.collapsedItems = [];

      // The expanded/collapsed items list didn't match the current data, the saved scrolling position is likely invalid
      if (this.collapsedItems.length != counts) {
        this.lastVScrollPosition = 0;
      }
    }
    
    // Restore the vertical scrolling position
    const content = this.grid?.element.querySelectorAll(".e-content");
    if (content && content.length > 0) {
      content[0].scrollTop = this.lastVScrollPosition;
    }
  }

  // Triggers when dataSource is loaded to the Grid (?).
  handleDataBound() {
    //console.log('*** in SShipping dataBound.  DataLen: ', this.grid?.dataSource instanceof Array ? this.grid?.dataSource.length : 0);
    this.lastGroupItems = [];
    this.restoreCollapseExpand();
  }
    
  // Draw a border to highlight the copied cell.
  setCopiedCellBorder(enabled: boolean) {
    if (this.copiedCellIndex.row !== -1 && this.copiedCellIndex.col !== -1) {
      const cell = this.grid?.getCellFromIndex(this.copiedCellIndex.row, this.copiedCellIndex.col);
      cell?.setAttribute('style', enabled ? 'outline: dashed 1px #0870d8;outline-offset: -2px;' : 'outline: none;');
    }
  }

  // Draw a border to highlight the copied cell.
  setCopiedCellsBorders(enabled: boolean) {
    //console.log('*********************************setCopiedCellsBorders ', enabled, this.copiedCellIndexes);
    this.copiedCellIndexes?.forEach((item: ISelectedCell) => {
      item?.cellIndexes?.forEach((cellIndex: number) => {
        const cell = this.grid?.getCellFromIndex(item.rowIndex, cellIndex);
        //console.log('**************** cell ', cell);
        cell?.setAttribute('style', enabled ? 'outline: dashed 1px #0870d8;outline-offset: -2px;' : 'outline: none;');    
      });
    });
  }

  // Check the copied and selected Idxes and enable/disable the Copy and Paste buttons.
  enableCopyAndPaste(isEnable: boolean) {
    this.grid?.enableToolbarItems(['copy'], isEnable && this.selectedCellIndex.row !== -1 && this.selectedCellIndex.col !== -1);
    this.grid?.enableToolbarItems(['paste'], isEnable && 
                                             this.copiedCellIndex.row !== -1 && this.copiedCellIndex.col !== -1 && // no cell selected
                                             (this.copiedCellIndex.row != this.selectedCellIndex.row || this.copiedCellIndex.col != this.selectedCellIndex.col)); // no copy to self.
  }

  // Check the grouping columns and enable/disable the 'Collapse all' button.
  enableCollapseAll(isEnable: boolean) {
    this.grid?.enableToolbarItems(['collapse'], isEnable && 
                                             this.grid?.groupSettings.columns != undefined && this.grid?.groupSettings.columns?.length > 0);
    //console.log("*** grouping cols len:", this.constructor.name, Number(this.grid?.groupSettings.columns?.length))
  }

  handleActionBegin(args, childKey) {
    // Must clear copy and paste settings if user filters or sorts data
    if (args.requestType === 'filtering' || args.requestType === 'sorting') {
      this.collapsedItems = saveCollapsedItems(this.grid, this.mkUserKey(childKey), this.saveExpandedItems);
      
      // Rows change, these indices become invalid
      this.selectedCellIndex = { row: -1, col: -1 };
      this.copiedCellIndex = { row: -1, col: -1 };
      this.copiedCellColumnName = '';
      this.enableCopyAndPaste(false);
    }
    else if(args.requestType === 'grouping' || args.requestType === 'ungrouping') {
      this.enableCollapseAll(true);
    }
  }

  // Triggered after a cell being selected.
  // 'args' .
  handleCellSelected(args) {
    // console.log('*** handleCellSelected:', args, args.selectedRowCellIndex);
    this.selectedCellIndex = { row: args.cellIndex.rowIndex, col: args.cellIndex.cellIndex };
    this.enableCopyAndPaste(true);
  }

  saveScrollingPosition() {
    // const content = this.grid?.element.querySelectorAll(".e-content");
    // if (content && content.length > 0) {
    //   console.log('saveScrollingPosition', content[0].scrollTop);
    //   this.lastVScrollPosition = content[0].scrollTop;
      if (this.scrollingPositionKey !== '') {
        localStorage.setItem(this.mkUserKey(this.scrollingPositionKey), JSON.stringify(this.lastVScrollPosition));
      }
    // }
  }

  // Handle toolbar clicked event.  
  // BaseClass returns false when it does not know how to handle an event 
  //   to let the derived class knows it need to handle it.
  // If singleCell is set, allowing only one source cell.
  async handleToolbarClicked(args: ClickEventArgs, singleSourceCell: boolean = true): Promise<boolean> {
    //console.log('*** Base Handle toolbarClicked event: ', args.item.id, singleSourceCell);

    if (args.item.id === 'test') {
      console.log("Hook for testing.");
      return true;
    }
    else if (args.item.id === 'collapse') {
      if (this.grid?.groupSettings.columns && this.grid?.groupSettings.columns?.length > 0)
        this.grid?.groupCollapseAll();
      return true;
    }
    else if (args.item.id === 'copy') {
      if (singleSourceCell == true) {
        if (this.copiedCellColumnName == GRID_STATUS_COLUMN_LABELS.lastComment) {
          showNotification('This column does not support Copy & Paste.', NotificationType.error);
        }
        else {
          // Clear current cell border
          this.setCopiedCellBorder(false);

          // stored copied cell index for pasting later.
          this.copiedCellIndex = this.selectedCellIndex;
          this.copiedCellColumnName = this.grid == undefined ? '' : this.grid.columns[this.copiedCellIndex.col]['field'];
          // Enable Paste and set the border for the copied cell.
          //this.grid?.enableToolbarItems(['paste'], true);
          this.setCopiedCellBorder(true);
        }
      }
      // Multipe-cell copy for SDeliverablesInfo
      else {
        // Clear current cell border
        this.setCopiedCellsBorders(false);
        // stored copied cell index for pasting later.
        this.copiedCellIndexes = this.grid?.getSelectedRowCellIndexes();
        this.copiedCellIndex = this.selectedCellIndex;
        this.copiedCellColumnName = this.grid == undefined ? '' : this.grid.columns[this.copiedCellIndex.col]['field'];
        // Enable Paste and set the border for the copied cell.
        //this.grid?.enableToolbarItems(['paste'], true);
        this.setCopiedCellsBorders(true);        
      }
      return true;
    }
    return false;
  }

  // Setup grid columns (label, width, visibility) and their collapsed states.
  setupColumnNCollapse(colWidths: string[], headingMap: {[key:string]: any}, collapseKey: string) {

    this.initialWidths = colWidths;
    this.columnNames = Object.keys(headingMap); // data column
    this.columnLabels = this.columnNames.map((item) => {
      const itemValue = headingMap[item];
      return itemValue['label'];
    });
    this.columnVisibility = GetDefaultColumnVisibilityByRoles(headingMap, this.columnNames, this.authStore.user.roles);
    
    // Load last collapsed grouping.
    const collapsedItems = readFrLocalStorage(this.mkUserKey(collapseKey));
    console.log('*** setupColumnNCollapse collapsedItems:', collapsedItems);
    if (collapsedItems !== '') {
      try {
        this.collapsedItems = JSON.parse(collapsedItems);
      }
      catch (err) {
        console.log(`Failed to read collapsedItems for ${collapseKey} from local storage.`, err);        
      }
    }
  }

  getCurrentViewData(): any {
    const currentView = this.grid?.currentViewData['records'];
    if (currentView) {
      return currentView;
    }
    return this.grid?.getCurrentViewRecords();
  }

  myGroupCaptionTemplate(args) {
    //console.log('***** in myGroupCaptionTemplate:', args, args.items.childLevels);

    let recs: Array<SInformationDTO>;
    let key = '';
    const itemCounts = args.count;

    if (args.items.childLevels == undefined) {
      recs = args.items;
    }
    else {
      recs = args.items.records;
    }

    key = args.headerText + ': ' + recs[0][args.field];

    // Add or Update the item at this level
    if (args.items.childLevels !== undefined) {
      const oldItem = this.lastGroupItems.find(x => x.level == args.items.childLevels);
      if (oldItem) {
        oldItem.caption = key;
      }
      else {
        this.lastGroupItems.push({ level: args.items.childLevels, caption: key });
      }
    }

    // Find upper levels, make the prefix
    let prefix = '';
    let findLvl = args.items.childLevels == undefined ? 0 : args.items.childLevels + 1;
    do {
      const upperItem = this.lastGroupItems.find(x => x.level == findLvl);
      if (upperItem) {
        prefix = upperItem.caption + ' > ' + prefix;
      }
      else {
        break;
      }
      findLvl++;
    } while (findLvl < this.columnNames.length - 3);
    key = prefix + key;

    // Trailed by the number of items
    key += ' - ' + itemCounts.toString() + ' item' + (itemCounts > 1 ? 's' : '');    

    return (
      <div>{key}</div>
    );
  }

  // Create a user-specific key by append the userId to the key.
  public mkUserKey = (keyStr: string): string => {
    // return `UID_${this.userID}_${keyStr}`
    return mkUserKey(this.userID, keyStr);
  }

  abstract render(): JSX.Element;

  // This can be used in derived class.
  load = () => {
    //console.log("*** In base load");
  }

  created () {
    //console.log("*** In base created.");
  }


  // Template for variance column
  varianceTemplate = (props) => {
    const { variance } = props;
    const val = Number(variance) > 0 ? '+' + variance : variance;
    return (
      <div>
        {val}
      </div>
    );
  }
}

// Save the collapsed items to local storage
// If saveExpandedItems is defined and true, save the expanded items instead
export const saveCollapsedItems = (grid: GridComponent | null, keyName: string, saveExpandedItems?: boolean) => {
  const savedCollapsedItems: Array<CollapsedItem> = [];
  if (grid) {
    const collapsedItems = grid.element.querySelectorAll(saveExpandedItems ? '.e-recordplusexpand' : '.e-recordpluscollapse'); // the collapsed arrow has class='.e-recordpluscollapse'.
    console.log(`*** saveCollapsedItems collapsedItems for ${keyName}:`, saveExpandedItems, collapsedItems);
    if (collapsedItems) {
      collapsedItems.forEach(collapsedEle => {
        const item = collapsedEle.getAttribute('ej-mappingvalue');
        if (item) {
          const parentEle = collapsedEle.parentElement;
          if (parentEle) {
            const itemCustomName = parentEle.innerText.substring(0, parentEle.innerText.indexOf(' - ')).replace(/[^\x20-\x7E]/g, '');
            //console.log('********************** find parent', item, ' - ', itemCustomName);
            savedCollapsedItems.push({MappingName: item, CustomName: itemCustomName});
          }
        }
      });
    }
    savedCollapsedItems.reverse();
    localStorage.setItem(keyName, JSON.stringify(savedCollapsedItems));
  }
  return savedCollapsedItems;
}


// Restore the collapse states
export const restoreCollapsedItems = (grid: GridComponent | null, collapsedItems: Array<CollapsedItem>, restoreExpandedItems?: boolean): number => {
  //console.log("*** Restore: ", collapsedItems);
  let counts = 0;
  const key = restoreExpandedItems ? '.e-recordpluscollapse[ej-mappingvalue="' : '.e-recordplusexpand[ej-mappingvalue="';
  if (grid) {
    collapsedItems.forEach(val => {
      const elements = grid?.element.querySelectorAll(key + val.MappingName + '"]');
      elements?.forEach(elem => {
        if (elem && elem.parentElement) {
          let customName = elem.parentElement.innerText;
          customName = customName.substring(0, customName.indexOf(' - ')).replace(/[^\x20-\x7E]/g, '');
          if (customName == val.CustomName) {
            //console.log('*** collapse', val.CustomName, elem);
            // Collapse this element
            try {
              grid?.groupModule.expandCollapseRows(elem);
              counts++;
            }
            catch (err) {
              console.log('****** Exception in restoreCollapsedItems:', val.CustomName, err);
            }
          }
        }
      });
    });
  }
  return counts;
}

// Get default column visibility for roles by looking up a dict
export const GetDefaultColumnVisibilityByRoles = (dict: any, columnNames: Array<string>, roles: Array<string>): Array<boolean> => {
  const columnVisibility = columnNames.map((item) => {
    const itemValue = dict[item];
    if (itemValue['visible'] === undefined) {
      return true;  // If undefined then visible = true for all roles
    }
    // If not undefined then value is a dictionary of roles for which this column is visible
    const visibleRoles = itemValue['visible'];
    let visible = false;
    for (const role of roles) {
      if (role in visibleRoles) {
        visible = true;
        break;
      }
    }
    return visible;
  });
  return columnVisibility;
}

// Scan grid for tooltips and try to close them.
// Note: does not seem to work.
export const closeAllTooltipss = (grid: GridComponent | null) => {
  if (grid != undefined) {
    // Scan through all rows for tooltips.
    const rows = grid.getRows();
    if (rows != undefined) {
      rows.forEach((row) => {
        //Note: this does not seem to be able to find the stuck tooltips
        const tooltips = row.querySelectorAll('.custom-tooltip');
        //console.log('*** tooltips:', tooltips);
        tooltips?.forEach((element) => {
          try {
            console.log('********** Closing tooltip:', element);
            (element as any).ej2_instances[0].destroy(); // destroy the tooltip when unmount.
          } 
          catch (exc) {
            console.log('********** Could not close tooltip: ', element);
          }
        });
      });
    }
  }
};


export default SBase;
